/* Lightweight Accessibility Toolbar for Shopify
   - Text +/-
   - Hoher Kontrast
   - Graustufen
   - Links unterstreichen
   - Lesbare Schrift
   - Zurücksetzen
   Saves state in localStorage. No external deps. */

(function () {
  const LS_KEY = 'a11y_toolbar_state_v1';

  // --- helpers ---
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const on = (el, ev, fn) => el.addEventListener(ev, fn);
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const persisted = JSON.parse(localStorage.getItem(LS_KEY) || '{}');

  // Apply root font-size scaling safely
  function getRootFontPx() {
    const cs = getComputedStyle(document.documentElement);
    const fs = cs.fontSize; // e.g., "16px"
    return parseFloat(fs) || 16;
  }
  function setScale(scale) {
    // Scale from 0.8 to 1.6 (80%–160%)
    const s = clamp(scale, 0.8, 1.6);
    document.documentElement.style.fontSize = (getRootFontPx() * s / 1).toFixed(2) + 'px';
    state.scale = s;
    saveState();
    updatePressed();
  }

  // Toggle class helpers
  function toggleClass(flag, cls) {
    document.documentElement.classList.toggle(cls, !!flag);
    state[cls] = !!flag;
    saveState();
    updatePressed();
  }

  // Persist / restore
  const state = {
    scale: persisted.scale || 1,
    'a11y-contrast': !!persisted['a11y-contrast'],
    'a11y-grayscale': !!persisted['a11y-grayscale'],
    'a11y-underline': !!persisted['a11y-underline'],
    'a11y-readable': !!persisted['a11y-readable'],
  };
  function saveState() {
    localStorage.setItem(LS_KEY, JSON.stringify({
      scale: state.scale,
      'a11y-contrast': state['a11y-contrast'],
      'a11y-grayscale': state['a11y-grayscale'],
      'a11y-underline': state['a11y-underline'],
      'a11y-readable': state['a11y-readable'],
    }));
  }

  // Inject minimal CSS
  const css = `
  .a11y-toolbar{position:fixed;left:1rem;bottom:1rem;z-index:999999;
    background:#111;color:#fff;border-radius:.75rem;box-shadow:0 8px 24px rgba(0,0,0,.25);
    font:14px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Inter,Arial,sans-serif;overflow:hidden}
  .a11y-toolbar[hidden]{display:none}
  .a11y-toolbar__row{display:flex;gap:.25rem;padding:.5rem}
  .a11y-btn{appearance:none;border:0;background:#222;color:#fff;padding:.5rem .6rem;border-radius:.5rem;cursor:pointer}
  .a11y-btn:hover{background:#2b2b2b}
  .a11y-btn:focus{outline:2px solid #69f;outline-offset:2px}
  .a11y-btn[aria-pressed="true"]{background:#3a3a3a;box-shadow:inset 0 0 0 2px #69f}
  .a11y-sep{width:1px;background:#333;margin:.5rem 0}
  .a11y-toolbar__label{padding:.4rem .6rem .2rem;font-weight:600;opacity:.9}

  /* High contrast mode (tries to be safe without inverting images) */
  .a11y-contrast body{background:#000 !important;color:#fff !important}
  .a11y-contrast a{color:#9cf !important}
  .a11y-contrast *{border-color:#888 !important}
  .a11y-contrast .btn, .a11y-contrast button, .a11y-contrast [role="button"]{background:#111 !important;color:#fff !important}

  /* Grayscale (don't grayscale images/videos/logos) */
  .a11y-grayscale html{filter:grayscale(1)}
  .a11y-grayscale img, .a11y-grayscale video, .a11y-grayscale svg{filter:none !important}

  /* Underline links */
  .a11y-underline a{text-decoration:underline !important}

  /* Readable font */
  .a11y-readable *{font-family:system-ui,-apple-system,Segoe UI,Roboto,Inter,Arial,sans-serif !important}

  /* Compact on mobile */
  @media (max-width: 480px){
    .a11y-toolbar{left:.5rem;right:.5rem;bottom:.5rem}
    .a11y-toolbar__row{flex-wrap:wrap}
  }
  `;
  const style = document.createElement('style');
  style.setAttribute('data-a11y-toolbar', 'true');
  style.appendChild(document.createTextNode(css));
  document.head.appendChild(style);

  // Toolbar UI
  const bar = document.createElement('div');
  bar.className = 'a11y-toolbar';
  bar.setAttribute('role', 'region');
  bar.setAttribute('aria-label', 'Barrierefreiheit Toolbar');

  bar.innerHTML = `
    <div class="a11y-toolbar__label">Barrierefreiheit</div>
    <div class="a11y-toolbar__row" role="toolbar" aria-label="Bedienelemente">
      <button class="a11y-btn" type="button" data-action="font-dec" aria-label="Text verkleinern">A−</button>
      <button class="a11y-btn" type="button" data-action="font-inc" aria-label="Text vergrößern">A+</button>

      <span class="a11y-sep" aria-hidden="true"></span>

      <button class="a11y-btn" type="button" data-action="contrast" aria-label="Hoher Kontrast" aria-pressed="false">Kontrast</button>
      <button class="a11y-btn" type="button" data-action="grayscale" aria-label="Graustufen" aria-pressed="false">Graustufen</button>

      <span class="a11y-sep" aria-hidden="true"></span>

      <button class="a11y-btn" type="button" data-action="underline" aria-label="Links unterstreichen" aria-pressed="false">Links</button>
      <button class="a11y-btn" type="button" data-action="readable" aria-label="Lesbare Schriftart" aria-pressed="false">Schrift</button>

      <span class="a11y-sep" aria-hidden="true"></span>

      <button class="a11y-btn" type="button" data-action="reset" aria-label="Zurücksetzen">Reset</button>
    </div>
  `;
  document.body.appendChild(bar);

  // Restore state on load
  function restore() {
    setScale(state.scale || 1);
    document.documentElement.classList.toggle('a11y-contrast', state['a11y-contrast']);
    document.documentElement.classList.toggle('a11y-grayscale', state['a11y-grayscale']);
    document.documentElement.classList.toggle('a11y-underline', state['a11y-underline']);
    document.documentElement.classList.toggle('a11y-readable', state['a11y-readable']);
    updatePressed();
  }

  // Update aria-pressed for toggles
  function updatePressed() {
    const map = {
      contrast: state['a11y-contrast'],
      grayscale: state['a11y-grayscale'],
      underline: state['a11y-underline'],
      readable: state['a11y-readable'],
    };
    bar.querySelectorAll('[data-action]').forEach(btn => {
      const a = btn.getAttribute('data-action');
      if (a in map) btn.setAttribute('aria-pressed', map[a] ? 'true' : 'false');
    });
  }

  // Click handlers
  on(bar, 'click', (e) => {
    const btn = e.target.closest('[data-action]');
    if (!btn) return;
    const action = btn.getAttribute('data-action');

    if (action === 'font-inc') return setScale((state.scale || 1) + 0.1);
    if (action === 'font-dec') return setScale((state.scale || 1) - 0.1);
    if (action === 'contrast') return toggleClass(!state['a11y-contrast'], 'a11y-contrast');
    if (action === 'grayscale') return toggleClass(!state['a11y-grayscale'], 'a11y-grayscale');
    if (action === 'underline') return toggleClass(!state['a11y-underline'], 'a11y-underline');
    if (action === 'readable') return toggleClass(!state['a11y-readable'], 'a11y-readable');

    if (action === 'reset') {
      state.scale = 1;
      document.documentElement.style.fontSize = '';
      ['a11y-contrast','a11y-grayscale','a11y-underline','a11y-readable'].forEach(cls=>{
        document.documentElement.classList.remove(cls);
        state[cls] = false;
      });
      saveState();
      updatePressed();
    }
  });

  // Keyboard: Space/Enter activates focused button by default (native)

  // Initialize
  restore();
})();
